// App-level navigation shell. Wraps the IonSplitPane and picks the right
// rail based on the signed-in user's role:
//
//   - isAdmin === true  → OperatorRail (deep teal, labeled, collapsible)
//   - otherwise         → CrmRail      (slim white, icon-only)
//
// The rail itself is presentational; this component supplies the workspace
// list, the current user info, the navigate handler, and the active item.
//
// Impersonation is wired but dormant — once we have a real impersonation
// flow, set `impersonation` to the session data and the banner renders
// above the content automatically.

import { useMemo } from 'react';
import { IonMenu, IonRouterOutlet, IonSplitPane } from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';

import { CrmRail } from './CrmRail';
import { OperatorRail } from './OperatorRail';
import { ImpersonationBanner } from './ImpersonationBanner';
import { CRM_FOOTER, CRM_PRIMARY, OPERATOR_GROUPS, pickActiveId } from './config';
import RouteBoundary from './RouteBoundary';
import type { NavItem, RailUser, Workspace } from './types';
import { useNavCollapse } from './useNavCollapse';
// Local Rollover Rep brand icon. Bundled via webpack (CRA's image loader
// hashes the filename for cache busting) so the rail loads it instantly
// with no external request, and we control any future updates by
// dropping a new PNG over this one.
import rrLogo from './icons/rr-logo.png';
import { logoutUser } from '../../firebase';

// Width of the OperatorRail when collapsed — keeps in sync with
// --rr-rail-ops-w-min in tokens.css. Hardcoded as a number here because
// we set it as an inline style on the IonMenu, which doesn't go through
// the CSS-variable cascade.
const OPS_WIDTH_FULL = '240px';
const OPS_WIDTH_COLLAPSED = '64px';
const CRM_WIDTH = '56px';
const OPERATOR_COLLAPSE_STORAGE_KEY = 'rr.nav.operator.collapsed';

interface ImpersonationContext {
  tenantId: string;
  tenantName: string;
  asUser: string;
  reason: string;
  startedAt: string;
}

interface Props {
  /** Whether the signed-in user is an internal operator. Drives rail choice
   *  AND the workspace kind shown in the switcher. */
  isAdmin: boolean;
  /** Signed-in user's email (used to derive the footer label + initials).
   *  Empty string is OK — the rail footer just won't render. */
  email: string;
  /** Display name / role string for the operator-rail footer. Optional —
   *  falls back to the email's local-part. */
  displayName?: string;
  role?: string;
  /** When set, the banner mounts above the page content and tints the
   *  rail. The current app never sets this; the prop exists so a future
   *  impersonation flow can flip it without revisiting the shell. */
  impersonation?: ImpersonationContext | null;
  /** Override the CRM rail's primary / footer nav items. Used to render a
   *  locked-down rail (e.g. recordings-only agents get just Recordings +
   *  Settings). Ignored when isAdmin (operator rail). Defaults to the full
   *  CRM_PRIMARY / CRM_FOOTER. */
  crmItems?: NavItem[];
  crmFooter?: NavItem[];
  /** Routes / content to render in the main outlet. */
  children: React.ReactNode;
}

/** Derive a friendly display name from an email like "tyler@rolloverrep.com"
 *  → "Tyler". Best-effort only; the caller can pass `displayName` explicitly
 *  to override. */
function nameFromEmail(email: string): string {
  if (!email) return '';
  const local = email.split('@')[0] || '';
  if (!local) return email;
  return local.charAt(0).toUpperCase() + local.slice(1);
}

function initialsFromEmail(email: string): string {
  if (!email) return '??';
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (local.slice(0, 2) || '??').toUpperCase();
}

// Resolved by webpack at build time; same shape as a string URL.
const BRAND_LOGO_URL: string = rrLogo;

/** Single workspace for now — the Rollover Rep org itself. The switcher
 *  detects `workspaces.length <= 1` and renders this as a non-interactive
 *  brand mark, so the chrome looks right without us having multi-tenancy
 *  built out yet. */
function workspaceFor(isAdmin: boolean): Workspace {
  return {
    id: 'rolloverrep',
    name: 'Rollover Rep',
    kind: isAdmin ? 'operator' : 'customer',
    initials: 'RR',
    color: '#DC3C3F',
    subtitle: isAdmin ? 'Operator Console' : 'Workspace',
    // logoUrl wins over initials in WorkspaceMark — the rail head shows the
    // real brand icon instead of the "RR" letter pair. Falls back to
    // initials automatically if the image fails to load.
    logoUrl: BRAND_LOGO_URL,
  };
}

const AppShell: React.FC<Props> = ({
  isAdmin,
  email,
  displayName,
  role,
  impersonation,
  crmItems,
  crmFooter,
  children,
}) => {
  const history = useHistory();
  const location = useLocation();

  const workspaces = useMemo(() => [workspaceFor(isAdmin)], [isAdmin]);
  const activeId = pickActiveId(location.pathname, isAdmin);

  // Mirror OperatorRail's collapse state here so the IonMenu wrapper width
  // can track it. useNavCollapse broadcasts changes within the tab, so
  // both this hook call and the one inside OperatorRail stay in lockstep.
  const { collapsed: opsCollapsed } = useNavCollapse(OPERATOR_COLLAPSE_STORAGE_KEY, false);

  // The OperatorRail footer wants a user object. CrmRail doesn't display
  // one (settings + tooltips are the whole identity affordance). Only build
  // the object when we'll actually use it.
  const railUser: RailUser | undefined = isAdmin
    ? {
        name: displayName || nameFromEmail(email) || email || 'Operator',
        role: role || (isAdmin ? 'Operator' : 'Member'),
        initials: initialsFromEmail(email),
        // Red avatar telegraphs internal staff — matches the design example.
        avatarColor: 'red',
      }
    : undefined;

  function navigate(item: NavItem) {
    if (item.to) history.push(item.to);
  }

  // Workspace switcher selection — today there's only one workspace, but if
  // the user ever picks a different one we treat it as plain navigation
  // (impersonation lives behind a different affordance per the design).
  function onSwitchWorkspace(_workspaceId: string) {
    // No-op for now; a multi-tenant build would update session state here.
  }

  // The operator-rail user pill in the footer doubles as the account menu
  // trigger. We don't have a proper user menu surface yet, so for now this
  // logs out — it's the only signed-in action available. When we build a
  // real account menu (settings, switch role, sign out), swap this to open
  // that popover instead.
  async function onUserMenu() {
    if (window.confirm('Log out?')) {
      try { await logoutUser(); } catch { /* surface elsewhere */ }
    }
  }

  return (
    <IonSplitPane contentId="main" when="md">
      {/* Wrap in IonMenu — Ionic's split-pane integration looks for IonMenu
       * specifically and wires it up properly (sizing, mobile drawer
       * behavior, no flicker during route transitions). A bare slot="start"
       * div technically works but is fragile across Ionic versions and
       * was leaving the rail invisible.
       *
       * IonMenu's width is controlled via the --width custom property,
       * which we set per role via the .rr-rail-pane / .rr-rail-pane--ops
       * classes (see App.css). The rail itself stays a plain child. */}
      <IonMenu
        contentId="main"
        menuId="appnav"
        type="overlay"
        className={`rr-rail-pane${isAdmin ? ' rr-rail-pane--ops' : ' rr-rail-pane--crm'}`}
        // Inline --width beats Ionic's shadow-DOM default (304px). Without
        // this, IonMenu's host renders at 304px while the rail inside is
        // only 240px (ops) / 56px (crm), leaving a blank strip between the
        // rail and the content. CSS class writes weren't winning — Ionic
        // sets defaults via shadow-DOM :host rules that load before our
        // class styles attach. Inline always wins.
        //
        // Width also tracks the OperatorRail's collapse state via the
        // mirrored useNavCollapse value above, so toggling the rail
        // tightens the IonMenu wrapper from 240→64px (no orphan strip).
        style={(() => {
          const w = isAdmin ? (opsCollapsed ? OPS_WIDTH_COLLAPSED : OPS_WIDTH_FULL) : CRM_WIDTH;
          return { '--width': w, '--min-width': w, '--max-width': w } as React.CSSProperties;
        })()}
      >
        {isAdmin ? (
          <OperatorRail
            workspaces={workspaces}
            activeWorkspaceId="rolloverrep"
            groups={OPERATOR_GROUPS}
            activeId={activeId}
            user={railUser}
            onNavigate={navigate}
            onSwitchWorkspace={onSwitchWorkspace}
            onUserMenu={onUserMenu}
          />
        ) : (
          <CrmRail
            workspaces={workspaces}
            activeWorkspaceId="rolloverrep"
            isImpersonating={!!impersonation}
            items={crmItems ?? CRM_PRIMARY}
            footerItems={crmFooter ?? CRM_FOOTER}
            activeId={activeId}
            onNavigate={navigate}
            onSwitchWorkspace={onSwitchWorkspace}
          />
        )}
      </IonMenu>

      {/* IonRouterOutlet IS the contentId="main" target — Ionic's page
       * stack manager needs it that way.
       *
       * animated={false}: Ionic's default route transition slides pages
       * in/out and was leaving the container blank when navigating
       * between routes that share heavy chrome (ProjectDetail →
       * /Projects/MyWork). With sliding off, new pages render immediately
       * in place. We lose the slide animation but gain reliability — the
       * user always sees the next page's content. */}
      <IonRouterOutlet
        id="main"
        animated={false}
        className={impersonation ? 'is-impersonating' : undefined}
      >
        {/* RouteBoundary is the LAST line of defense — any uncaught error
         * inside the outlet (route component crashing during render, etc)
         * lands here instead of leaving the user with a blank panel. */}
        <RouteBoundary>{children}</RouteBoundary>
      </IonRouterOutlet>
      {impersonation && (
        <ImpersonationBanner
          tenantName={impersonation.tenantName}
          asUser={impersonation.asUser}
          reason={impersonation.reason}
          sessionStartedAt={impersonation.startedAt}
          onExit={() => { /* caller wires this once impersonation is real */ }}
        />
      )}
    </IonSplitPane>
  );
};

export default AppShell;
